import { convivaAppTracker, setUserId, trackPageView } from '@convivainc/conviva-js-appanalytics';
import { IPlayerAdapter, PlayerType, VideoMetadataType } from '@/services/conviva/conviva.types';
import { BitmovinAdapter } from '@/services/conviva/adapter/bitmovin-adapter';
import { ShakaAdapter } from '@/services/conviva/adapter/shaka-adapter';
import { HLSAdapter } from '@/services/conviva/adapter/hls-adapter';
import { DashAdapter } from '@/services/conviva/adapter/dash-adapter';
import packageJson from '../../../package.json';

type Class<T = any> = new (...args: any[]) => T;

const playerAdapters: Record<PlayerType, Class<IPlayerAdapter>> = {
  [PlayerType.Bitmovin]: BitmovinAdapter,
  [PlayerType.Shaka]: ShakaAdapter,
  [PlayerType.HLS]: HLSAdapter,
  [PlayerType.DASH]: DashAdapter,
};

class ConvivaService {
  static initialMetadata: VideoMetadataType = {
    title: 'NA',
    isLive: false,
  };
  private customerKey = process.env.VUE_APP_CONVIVA_CUSTOMER_KEY || '';
  private player: any = null;
  private playerType: PlayerType | null = null;
  private playerAdapter: IPlayerAdapter | null = null;
  private viewerId = 'NA';
  private sessionReadyPromise: Promise<void> | null = null;
  private lastSessionConfig: { isLive: boolean; assetName: string } | null = null;
  metadata: VideoMetadataType = ConvivaService.initialMetadata;
  logging = false; // enable for debugging

  constructor() {
    this.initializeDpi();
  }

  initializeDpi() {
    try {
      convivaAppTracker({
        appId: 'thmanyah-tv',
        convivaCustomerKey: this.customerKey,
        appVersion: packageJson.version,
      });
      if (this.logging) {
        console.log('[conviva] DPI initialized successfully');
      }
    } catch (error) {
      // CORS or network errors during Conviva initialization should not break the app
      console.warn('[conviva] Failed to initialize DPI (CORS or network error):', error);
      console.warn('[conviva] Analytics will be disabled but video playback will continue');
    }
  }

  setUserId(userId: string) {
    // Ensure userId is never empty - use 'NA' as fallback
    const validUserId = userId && userId.trim() !== '' ? userId : 'NA';
    this.viewerId = validUserId;
    this.metadata.viewerId = validUserId;
    setUserId(validUserId);
    this.setContentInfo(this.metadata);
  }

  trackPageView(viewId: string) {
    trackPageView({ title: viewId });
  }

  createVsiSession({
    isLive = false,
    assetName = 'NA',
    playerType,
    contentId,
  }: {
    isLive?: boolean;
    assetName?: string;
    playerType?: PlayerType;
    /** Match id (live) or asset id (VOD); used as viewerId fallback when no user is logged in */
    contentId?: string;
  }) {
    try {
      if (this.playerAdapter) {
        try {
          this.playerAdapter.destroySession();
        } catch (error) {
          if (this.logging) {
            console.warn('[conviva] Failed to destroy existing session before creating new one', error);
          }
        }
        try {
          this.playerAdapter.unsetPlayer();
        } catch (error) {
          if (this.logging) {
            console.warn('[conviva] Failed to detach player before creating new session', error);
          }
        }
        this.playerAdapter = null;
        this.playerType = null;
        this.sessionReadyPromise = null;
      }

      // Use explicit playerType if provided, otherwise fall back to legacy logic
      // Legacy: Live content -> Bitmovin, VOD -> Shaka
      // New: Explicit playerType for HLS/Dash players
      const selectedPlayerType = playerType || (isLive ? PlayerType.Bitmovin : PlayerType.Shaka);
      const SpecificPlayerAdapter = playerAdapters[selectedPlayerType];
      this.playerAdapter = new SpecificPlayerAdapter();
      this.lastSessionConfig = { isLive, assetName };
      const sessionViewerId =
        this.viewerId && this.viewerId !== 'NA' && this.viewerId.trim() !== ''
          ? this.viewerId
          : contentId && String(contentId).trim() !== ''
            ? String(contentId).trim()
            : 'NA';
      this.metadata = {
        ...this.metadata,
        isLive,
        title: assetName,
        viewerId: sessionViewerId,
      };

      // No need to wait for device detection - it started in constructor
      // and will have completed by the time user navigates to video page
      this.playerAdapter.createSession({ assetName, viewerId: sessionViewerId, isLive });
      this.playerType = selectedPlayerType;
      this.setContentInfo(this.metadata);

      if (this.logging) {
        console.log(`[conviva] Session created: ${assetName} (${isLive ? 'live' : 'vod'}, ${selectedPlayerType})`);
      }
    } catch (error) {
      console.warn('[conviva] Failed to create VSI session:', error);
      console.warn('[conviva] Video playback will continue without analytics');
    }
  }

  destroyVsiSession() {
    if (!this.playerAdapter) {
      return;
    }
    this.playerAdapter.destroySession();
    this.sessionReadyPromise = null;
    this.lastSessionConfig = null;
  }

  setPlayer(newPlayer: any, videoElement?: HTMLVideoElement) {
    if (!this.playerAdapter) {
      if (this.logging) {
        console.warn('[conviva] setPlayer called but no adapter initialized (Conviva may have failed to initialize)');
      }
      return;
    }

    try {
      this.player = newPlayer;

      // HLS and Dash adapters need both player and videoElement
      // Bitmovin and Shaka adapters only need the player instance
      if (videoElement && (this.playerType === PlayerType.HLS || this.playerType === PlayerType.DASH)) {
        this.playerAdapter.setPlayer({ player: newPlayer, videoElement });
      } else {
        this.playerAdapter.setPlayer(newPlayer);
      }

      this.setContentInfo(this.metadata);

      if (this.logging) {
        console.log(`[conviva] Player attached: ${this.playerType}`);
      }
    } catch (error) {
      console.warn('[conviva] Failed to attach player:', error);
      console.warn('[conviva] Video playback will continue without analytics');
    }
  }

  reportAppBackgrounded() {
    if (!this.playerAdapter) return;
    try {
      this.playerAdapter.reportAppBackgrounded();
    } catch (error) {
      console.warn('[conviva] Failed to report app backgrounded:', error);
    }
  }

  reportAppForegrounded() {
    if (!this.playerAdapter) return;
    try {
      this.playerAdapter.reportAppForegrounded();
    } catch (error) {
      console.warn('[conviva] Failed to report app foregrounded:', error);
    }
  }

  sendCustomEvent(eventName: string, attributes: Record<string, any> | string = {}): void {
    if (!this.playerAdapter) {
      if (this.logging) {
        console.warn('[conviva] Cannot send custom event: no active player adapter');
      }
      return;
    }

    const attrs: Record<string, any> = typeof attributes === 'string' ? { manifest_url: attributes } : attributes || {};

    const normalizedAttributes: Record<string, string> = {};
    Object.entries(attrs).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      if (value === null) {
        normalizedAttributes[key] = 'null';
        return;
      }
      if (typeof value === 'string') {
        normalizedAttributes[key] = value;
        return;
      }
      if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        normalizedAttributes[key] = String(value);
        return;
      }
      if (value instanceof Error) {
        normalizedAttributes[key] = value.message;
        return;
      }
      try {
        normalizedAttributes[key] = JSON.stringify(value);
      } catch {
        normalizedAttributes[key] = String(value);
      }
    });

    try {
      this.playerAdapter.sendCustomEvent(eventName, normalizedAttributes);
      if (this.logging) {
        console.log('[conviva] Custom event sent', eventName, normalizedAttributes);
      }
    } catch (error) {
      console.warn('[conviva] Failed to send custom event:', eventName, error);
    }
  }

  unsetPlayer() {
    if (!this.playerAdapter) {
      if (this.logging) {
        console.warn('[conviva] unsetPlayer called but no active adapter');
      }
      return;
    }
    this.destroyVsiSession();
    this.playerAdapter.unsetPlayer();
    this.playerAdapter = null;
    this.player = null;
    this.playerType = null;
    this.sessionReadyPromise = null;
    this.metadata = ConvivaService.initialMetadata;
  }

  restartVideoAnalytics() {
    if (!this.player) {
      throw new TypeError('Player is already unset');
    }
    if (!this.playerType || !this.playerAdapter) {
      throw new TypeError('Player is already unset');
    }
    if (!this.lastSessionConfig) {
      throw new TypeError('Session configuration is unavailable');
    }
    const player = this.player;
    const metadata = { ...this.metadata };
    const sessionConfig = this.lastSessionConfig;

    this.playerAdapter.destroySession();
    this.playerAdapter.unsetPlayer();
    this.playerAdapter = null;
    this.playerType = null;
    this.sessionReadyPromise = null;

    this.createVsiSession(sessionConfig);
    this.setPlayer(player);
    this.setContentInfo(metadata);
  }

  setContentInfo(metadata: VideoMetadataType) {
    const mergedCustom = {
      ...(this.metadata.custom || {}),
      ...(metadata.custom || {}),
    };
    this.metadata = {
      ...this.metadata,
      ...metadata,
      custom: mergedCustom,
    };
    if (!this.playerAdapter) {
      // stash metadata info until the player is set
      return;
    }
    this.playerAdapter.setContentInfo({
      isLive: this.metadata.isLive,
      title: this.metadata.title,
      viewerId: this.metadata.viewerId ?? this.viewerId,
      custom: this.metadata.custom,
    });
  }
}

// export the singleton instance of ConvivaService
export const convivaService = new ConvivaService();
